import { MOCK_LIVE_STREAMS } from '@/features/marketing/mockData/streamEvents'
import { MOCK_SQUADRON } from '@/features/marketing/mockData/squadron'
import { BlacklineLogoMark } from '@/features/marketing/components/BlacklineLogoMark'

/**
 * Live tab mockup — mirrors the in-product Live tab where members
 * see who's streaming right now plus any scheduled stream events
 * about to flip live.
 *
 * Composition: tab header (squadron mark + tab pills) → 4 stream
 * tiles in a 2-col grid → "scheduled next" strip at the bottom.
 *
 * Why this composition: visitors don't read live streaming as a
 * built-in feature — most platforms force a separate Twitch tab.
 * Showing the in-app surface in the squadron context closes that
 * loop visually.
 */
export function LiveTabMockup() {
  return (
    <div className="p-4">
      {/* Tab header — squadron context + tabs */}
      <div className="flex items-center justify-between border-b border-border/60 pb-3">
        <div className="flex items-center gap-2">
          <BlacklineLogoMark size={28} />
          <div>
            <p className="font-display text-sm font-extrabold leading-none text-fg">
              {MOCK_SQUADRON.name}
            </p>
            <p className="font-display mt-0.5 text-[9px] tracking-[0.22em] text-fg-subtle uppercase">
              Squadron
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <TabPill label="Dashboard" />
          <TabPill label="Events" />
          <TabPill label="Live" active />
        </div>
      </div>

      {/* Live now strip */}
      <div className="mt-4 flex items-center justify-between">
        <p className="font-display text-[10px] font-extrabold tracking-[0.25em] text-success uppercase">
          <span className="mr-1.5 inline-block size-1.5 rounded-full bg-success align-middle" />
          {MOCK_LIVE_STREAMS.length} streaming now
        </p>
        <p className="font-display text-[10px] tracking-[0.22em] text-fg-subtle uppercase">
          Auto-pulls from Twitch
        </p>
      </div>

      {/* Stream grid */}
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {MOCK_LIVE_STREAMS.map((s) => (
          <StreamCard key={s.twitchLogin} stream={s} />
        ))}
      </div>

      {/* Scheduled strip */}
      <div className="mt-4 rounded-lg border border-border/60 bg-bg/40 px-3 py-2.5">
        <p className="font-display text-[10px] font-extrabold tracking-[0.22em] text-primary uppercase">
          Scheduled · auto-flips to live
        </p>
        <p className="mt-1 text-[11px] text-fg-muted">
          <span className="font-display font-semibold text-fg">Friday CS2 night</span>{' '}
          · 4 streamers · starts 20:00 BST · 2h 11m
        </p>
      </div>
    </div>
  )
}

function TabPill({ label, active }: { label: string; active?: boolean }) {
  return (
    <span
      className={`font-display rounded-md px-2 py-1 text-[10px] font-bold tracking-[0.2em] uppercase ${
        active
          ? 'bg-primary/15 text-primary'
          : 'text-fg-subtle'
      }`}
    >
      {label}
    </span>
  )
}

function StreamCard({ stream }: { stream: (typeof MOCK_LIVE_STREAMS)[number] }) {
  const hours = Math.floor(stream.startedHoursAgo)
  const minutes = Math.round((stream.startedHoursAgo - hours) * 60)
  const uptime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface/40">
      {/* Thumbnail with live pip + viewer count */}
      <div className="relative aspect-video w-full overflow-hidden bg-black/40">
        <img
          src={stream.thumbnailUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <span className="font-display absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-sm bg-danger px-1.5 py-[1px] text-[9px] font-extrabold tracking-[0.18em] text-white uppercase">
          <span className="size-1 rounded-full bg-white" />
          Live
        </span>
        <span className="font-display absolute right-1.5 top-1.5 rounded-sm bg-black/70 px-1.5 py-[1px] text-[9px] font-bold text-white tabular-nums">
          {stream.viewerCount.toLocaleString()}
        </span>
        <span className="font-display absolute bottom-1.5 right-1.5 rounded-sm bg-black/70 px-1.5 py-[1px] text-[9px] text-white/80 tabular-nums">
          {uptime}
        </span>
      </div>
      {/* Title + meta */}
      <div className="flex items-start gap-2 p-2">
        <img
          src={stream.avatarUrl}
          alt=""
          className="size-7 shrink-0 rounded-full border border-border"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-1 text-[11px] font-semibold text-fg">
            {stream.streamTitle}
          </p>
          <p className="font-display mt-0.5 truncate text-[10px] text-fg-muted">
            {stream.streamerCallsign} · {stream.gameName}
          </p>
        </div>
      </div>
    </div>
  )
}
